import type { ReactNode } from 'react';
import { type BuildingRow, buildingGroups, buildingIcon } from '@/game/view/buildingsList';
import { VERBS } from '@/game/view/verbs';
import { useSimFrame } from './app/simFrame';
import { CollapseToggle, usePersisted } from './persisted';
import { VerbGlyph } from './VerbLabel';

interface BuildingsListPanelProps {
  selectedBuildingId: string | null;
  /** Select a building; `pan` (a plain click, not shift) also takes the camera there. */
  onSelectBuilding: (id: string, pan: boolean) => void;
  /** Replaces the panel title (the Units | Buildings tabs). */
  header?: ReactNode;
}

/**
 * Own buildings by city (left column, sharing the Units panel's slot): each
 * city's row heads the buildings in its territory with their level, workers
 * or stored units, hp while damaged, a dot when an upgrade is ready, and
 * sites with how far built. A click selects and goes there; shift-click only
 * selects.
 */
export function BuildingsListPanel({
  selectedBuildingId,
  onSelectBuilding,
  header,
}: BuildingsListPanelProps) {
  const [collapsed, setCollapsed] = usePersisted('buildingsCollapsed', false);
  const { view: state, rules, terrain, playerId, cityNumbers, names } = useSimFrame();
  const groups = collapsed
    ? []
    : buildingGroups(state, rules, terrain, playerId, cityNumbers, names);
  const ready = groups.reduce(
    (n, g) => n + g.rows.filter((r) => r.upgradable).length + (g.city?.upgradable ? 1 : 0),
    0
  );

  const select = (id: string, shift: boolean) => onSelectBuilding(id, !shift);

  return (
    <div className="panel pointer-events-auto flex max-h-full min-h-0 w-full flex-col p-3 text-xs">
      <div className="flex shrink-0 items-center justify-between gap-2">
        {header ?? <p className="panel-title px-1">Buildings</p>}
        {ready > 0 && (
          <span
            title={`${ready} ${ready === 1 ? 'building' : 'buildings'} can level up now`}
            className="ml-auto flex items-center gap-1 text-[11px] text-accent"
          >
            <LevelUpMark />
            {ready}
          </span>
        )}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          label="buildings"
        />
      </div>
      {!collapsed && (
        <div className="min-h-0 overflow-y-auto">
          <p className="mb-1 px-1 text-[11px] text-fg-subtle">
            Click to select and go there; shift-click only selects
          </p>
          {groups.length === 0 ? (
            <p className="px-1 text-fg-muted">No buildings</p>
          ) : (
            groups.map((g) => (
              <div key={g.cityId ?? 'outside'} className="mt-2">
                {g.city ? (
                  <Row
                    row={g.city}
                    label={g.label}
                    heading
                    selected={g.city.building.id === selectedBuildingId}
                    onSelect={select}
                  />
                ) : (
                  <p
                    title="Idle until your border covers them again"
                    className="mb-0.5 px-2 text-warn"
                  >
                    {g.label}
                  </p>
                )}
                <div className="space-y-0.5 border-l border-line pl-1.5 ml-2.5">
                  {g.rows.map((r) => (
                    <Row
                      key={r.building.id}
                      row={r}
                      label={r.name}
                      selected={r.building.id === selectedBuildingId}
                      onSelect={select}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LevelUpMark() {
  return (
    <span
      role="img"
      aria-label="Level up available"
      className="shrink-0 text-[11px] leading-none text-accent"
    >
      <VerbGlyph verb="levelUp" />
    </span>
  );
}

function Row({
  row,
  label,
  heading = false,
  selected,
  onSelect,
}: {
  row: BuildingRow;
  label: string;
  heading?: boolean;
  selected: boolean;
  onSelect: (id: string, shift: boolean) => void;
}) {
  const { building, level, load, hp, upgradable, progress } = row;
  const hurt = hp ? hp.hp / hp.max : 1;
  return (
    <button
      type="button"
      title={`${label}${level ? ` · ${level}` : ''}${upgradable ? ` · ${VERBS.levelUp.label.toLowerCase()} available` : ''}`}
      onClick={(e) => onSelect(building.id, e.shiftKey)}
      className={`flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-left transition ${selected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
    >
      <span className="w-4 shrink-0 text-center text-[11px] leading-none">
        {buildingIcon(building.type)}
      </span>
      <span className={`min-w-0 truncate ${heading ? 'font-medium text-fg' : 'text-fg-muted'}`}>
        {label}
        {progress !== null ? ' site' : ''}
      </span>
      {level && <span className="min-w-0 truncate text-[10px] text-fg-subtle">{level}</span>}
      <span className="flex-1" />
      {progress !== null && (
        <span className="flex shrink-0 items-center gap-1">
          <span className="h-1 w-8 overflow-hidden rounded-full bg-fill">
            <span
              className="block h-full rounded-full bg-accent"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </span>
          <span className="w-7 text-right text-[10px] tabular-nums text-fg-subtle">
            {Math.floor(progress * 100)}%
          </span>
        </span>
      )}
      {load && (
        <span
          title={
            load.label === 'workers'
              ? 'Workers (inside or hauling) / slots'
              : 'Units stored / capacity'
          }
          className={`shrink-0 text-[10px] tabular-nums ${load.used >= load.max && load.label === 'stored' ? 'text-warn' : 'text-fg-subtle'}`}
        >
          {load.label === 'workers' ? '👷' : '⚔'} {load.used}/{load.max}
        </span>
      )}
      {hp && progress === null && (
        <span
          title="Damaged"
          className={`shrink-0 text-[10px] tabular-nums ${hurt < 0.35 ? 'text-danger' : 'text-warn'}`}
        >
          {Math.ceil(hp.hp)}/{Math.ceil(hp.max)} hp
        </span>
      )}
      {upgradable && (
        <span title="Level up available" className="flex shrink-0 items-center">
          <LevelUpMark />
        </span>
      )}
    </button>
  );
}

export type RosterTab = 'units' | 'buildings' | 'workers';

/**
 * Units | Buildings | Workers: the left column's roster slot shows one at a time; this
 * pair of tabs replaces the shown panel's title.
 */
export function RosterTabs({
  tab,
  onTab,
  units,
  buildings,
  workers,
}: {
  tab: RosterTab;
  onTab: (tab: RosterTab) => void;
  units: number;
  buildings: number;
  workers: number;
}) {
  const tabs: Array<[RosterTab, string, number]> = [
    ['units', 'Units', units],
    ['buildings', 'Buildings', buildings],
    ['workers', 'Workers', workers],
  ];
  return (
    <div role="tablist" className="flex min-w-0 gap-0.5 overflow-hidden rounded-full bg-fill p-0.5">
      {tabs.map(([t, label, n]) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={t === tab}
          onClick={() => onTab(t)}
          className={`chip px-1.5 py-0.5 transition ${t === tab ? 'chip-selected font-medium' : 'bg-transparent text-fg-muted hover:bg-fill-hover'}`}
        >
          {label}
          <span className={t === tab ? 'opacity-60' : 'text-fg-subtle'}>{n}</span>
        </button>
      ))}
    </div>
  );
}
